/**
 * Spot tender catalogue — "Exclusif du ponctuel" (04/06/2026).
 *
 * Feature request (user, hst-game.com prod feedback):
 *   "Tu deviens un exclusif du ponctuel pour un client. Et tu dois être obligé de
 *   lui répondre à chaque fois. Donc avoir un chauffeur et un camion dispo pour
 *   ce projet. Grosse contrainte. 2 ou 3 jours pour réagir maxi. Couleur pour
 *   l'identifier. En contrepartie il faut que le montant soit pertinent et que
 *   la récurrence, bien qu'aléatoire sur la période de 90 j, apparaisse 3 fois
 *   par mois minimum. Si tu réponds pas, tu perds le contrat et tu encours une
 *   pénalité. Les niveaux de qualifications des chauffeurs et flotte plus
 *   standard pour ce genre."
 *
 * Game design summary:
 *   • Tender win → 90-day exclusivity engagement (renewable)
 *   • The system rolls "spot calls" 3-6 times per month, randomly distributed
 *   • Each spot call has a deadline of 3 days to respond (= dispatch driver+vehicle)
 *   • Missing a deadline triggers a penalty: -3 reputation + -800 € fine + 1 strike
 *   • 3 strikes = engagement terminated by the client (-10 reputation)
 *   • Single shipment payout: generous flat fee (€1,800–€4,500 per call)
 *   • Standard vehicle/driver requirements (Tier 1+, license C, no specialty)
 *   • UI color: deep purple #7C3AED (distinct from green AO, yellow special, etc.)
 */

/**
 * Helper: enrich a SPOT_TENDERS entry with the fields expected by the
 * generic TenderApplicationModal / computeProbability pipeline.
 * Spot tenders don't have a fixed `baseDailyRevenue` × `durationDays` —
 * we map them to the **average per-call payout** and a synthetic
 * 1-day "duration" so the existing pricing UI works without changes.
 */
function enrichSpotTender(t) {
  const [low, high] = t.callPayoutRange || [1800, 2400];
  const mid = Math.round((low + high) / 2);
  return {
    ...t,
    baseDailyRevenue: mid,        // surfaced on TenderCard + modale
    durationDays: 1,              // each call is a 1-day ponctuel
  };
}

export const SPOT_TENDERS = ([
  {
    id: 'spot-tender-ecomarche-nord',
    clientName: 'ÉcoMarché Nord',
    sector: 'distribution',
    tier: 'local',
    spot: true,
    engagementDays: 90,
    callPayoutRange: [1800, 2400],
    callsPerMonthMin: 3,
    callsPerMonthMax: 5,
    responseDeadlineDays: 3,
    requiresFleet: 2,
    requiresRseTier: 'none',
    requiresMedicalPct: 60,
    competitors: ['TransNord Express', 'Rapid\'Fret Solutions'],
    competitorPriceRange: [1600, 2100],
    narrative: {
      announce:
        "ÉcoMarché Nord ouvre un appel d'offres EXCLUSIF pour ses livraisons ponctuelles " +
        "imprévues (réassorts urgents, retours fournisseurs, événements promo). " +
        "Engagement 90 jours. Le retenu devient leur transporteur unique sur ces " +
        "demandes ad hoc — fréquence estimée 3 à 5 missions par mois, à honorer " +
        "sous 3 jours sinon pénalité contractuelle (800 € + perte de référencement " +
        "au 3ᵉ manquement).",
      win:
        "ÉcoMarché Nord vous accorde l'exclusivité du ponctuel. Restez disponible — " +
        "leurs demandes peuvent tomber n'importe quand sur les 90 prochains jours.",
      loss:
        "ÉcoMarché Nord a confié cette exclusivité à un concurrent. Ils ont " +
        "préféré un prestataire avec une dispo prouvée sur le ponctuel.",
    },
  },
  {
    id: 'spot-tender-fournitures-pro-ouest',
    clientName: 'Fournitures Pro Ouest',
    sector: 'industrie',
    tier: 'local',
    spot: true,
    engagementDays: 90,
    callPayoutRange: [2000, 2800],
    callsPerMonthMin: 3,
    callsPerMonthMax: 6,
    responseDeadlineDays: 3,
    requiresFleet: 2,
    requiresRseTier: 'none',
    requiresMedicalPct: 70,
    competitors: ['Rapid\'Fret Solutions', 'TransNord Express'],
    competitorPriceRange: [1700, 2400],
    narrative: {
      announce:
        "Fournitures Pro Ouest cherche un transporteur exclusif pour ses dépannages " +
        "industriels (pièces détachées, équipements urgents). Engagement 90 jours, " +
        "3 à 6 missions/mois, sous 3 jours sinon pénalité.",
      win:
        "Fournitures Pro Ouest vous retient comme prestataire exclusif du ponctuel. " +
        "Première mission probablement dans les jours qui viennent.",
      loss:
        "Fournitures Pro Ouest a finalement opté pour un autre prestataire mieux " +
        "implanté localement.",
    },
  },
  {
    id: 'spot-tender-evenementiel-prestige',
    clientName: 'Événementiel Prestige Sud',
    sector: 'événementiel',
    tier: 'regional',
    spot: true,
    engagementDays: 90,
    callPayoutRange: [3000, 4500],    // higher tier = higher payout
    callsPerMonthMin: 3,
    callsPerMonthMax: 4,
    responseDeadlineDays: 2,          // shorter deadline (event industry)
    requiresFleet: 3,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 80,
    competitors: ['LogiStar International', 'EuroSpot Logistics'],
    competitorPriceRange: [2800, 4200],
    narrative: {
      announce:
        "Événementiel Prestige (organisation de salons, congrès, mariages haut de " +
        "gamme) lance un AO d'exclusivité ponctuelle régionale. 3 à 4 missions/mois, " +
        "à honorer en 48 h max. Forte exigence qualité. Pénalité 800 € en cas " +
        "d'indisponibilité.",
      win:
        "Événementiel Prestige Sud vous confie l'exclusivité ponctuelle régionale. " +
        "Réputation forte à la clé si vous tenez la qualité.",
      loss:
        "Événementiel Prestige Sud a tranché en faveur d'un transporteur déjà " +
        "spécialisé sur l'événementiel haut de gamme.",
    },
  },
  {
    id: 'spot-tender-pharma-urgence',
    clientName: 'PharmaUrgence Île-de-France',
    sector: 'santé',
    tier: 'regional',
    spot: true,
    engagementDays: 90,
    callPayoutRange: [2800, 3800],
    callsPerMonthMin: 4,
    callsPerMonthMax: 6,
    responseDeadlineDays: 2,
    requiresFleet: 3,
    requiresRseTier: 'bronze',
    requiresMedicalPct: 85,
    competitors: ['Rapid\'Fret Solutions', 'MedExpress France'],
    competitorPriceRange: [2500, 3600],
    narrative: {
      announce:
        "PharmaUrgence IdF lance un AO pour ses transports d'urgence pharmaceutique " +
        "(transferts inter-hôpitaux, livraisons cliniques). Engagement 90 jours, " +
        "4 à 6 missions/mois, délai 48 h, exigence chauffeurs avec visite médicale " +
        "à jour et formation marchandises sensibles.",
      win:
        "PharmaUrgence IdF vous référence comme transporteur exclusif. " +
        "La régularité de leurs demandes assure un flux mensuel solide.",
      loss:
        "PharmaUrgence IdF a retenu un transporteur médical spécialisé. " +
        "Notre dossier RSE était bon mais l'expérience santé a manqué.",
    },
  },
]).map(enrichSpotTender);

/**
 * Helper: is a tender a "spot exclusivity" engagement ?
 */
export function isSpotTender(tender) {
  return !!(tender && tender.spot === true);
}
